import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Trash2, Zap, Loader2, Pause, Play, Plus, Eye, EyeOff, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const PROVIDERS = [
  {
    id: "openai",
    name: "OpenAI",
    role: "Thinking, hearing, the flowing line — and the understudy voice when the real one runs dry.",
    where: "platform.openai.com → API keys",
    placeholder: "sk-…",
  },
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    role: "The voices themselves — Keith Muoki and Yna Agalo speaking every word aloud.",
    where: "elevenlabs.io → Profile → API keys",
    placeholder: "sk_…",
  },
];

const STATE = {
  "in use": { label: "answering", cls: "border-[#2EC4B6]/40 bg-[#2EC4B6]/10 text-[#2EC4B6]" },
  "standing by": { label: "standing by", cls: "border-white/10 text-[#8A919D]" },
  resting: { label: "resting", cls: "border-[#E9C46A]/40 bg-[#E9C46A]/10 text-[#E9C46A]" },
  paused: { label: "paused", cls: "border-white/10 text-[#6E7582]" },
};

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const KeyRow = ({ k, i, last, onMove, onTest, onToggle, onRemove, testing }) => {
  const st = STATE[k.state] || STATE["standing by"];
  return (
    <li data-testid={`conn-row-${k.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
      <span className="w-5 shrink-0 font-mono text-xs text-[#6E7582]">{i + 1}</span>
      <div className="min-w-[180px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-[#F3EFE6]">{k.label}</span>
          <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${st.cls}`} data-testid={`conn-state-${k.id}`}>{st.label}</span>
          {k.env && <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] text-[#6E7582]">from .env</span>}
        </div>
        <p className="mt-1 font-mono text-[11px] text-[#6E7582]">{k.mask}</p>
        {k.state === "resting" && (
          <p
            className="mt-1 max-w-xl overflow-hidden text-xs text-[#E9C46A]"
            style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
            title={k.reason}
            data-testid={`conn-reason-${k.id}`}
          >
            Stepped aside {ago(k.resting_at)} — {k.quota ? "out of credit" : "refused"}. {k.reason}
          </p>
        )}
        {k.state !== "resting" && k.checked && (
          <p className="mt-1 text-xs text-[#6E7582]">{k.verified === false ? "Last test failed: " : ""}{k.checked}</p>
        )}
        {k.last_ok_at && k.state !== "resting" && (
          <p className="mt-0.5 font-mono text-[10px] text-[#4E555F]">answered {ago(k.last_ok_at)}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!k.env && (
          <>
            <button data-testid={`conn-up-${k.id}`} onClick={() => onMove(k, -1)} disabled={i === 0} aria-label="Move up" className="rounded-lg border border-white/10 p-2 text-[#8A919D] transition-colors hover:border-white/25 hover:text-[#F3EFE6] disabled:opacity-25"><ChevronUp size={14} /></button>
            <button data-testid={`conn-down-${k.id}`} onClick={() => onMove(k, 1)} disabled={last} aria-label="Move down" className="rounded-lg border border-white/10 p-2 text-[#8A919D] transition-colors hover:border-white/25 hover:text-[#F3EFE6] disabled:opacity-25"><ChevronDown size={14} /></button>
          </>
        )}
        <button data-testid={`conn-test-${k.id}`} onClick={() => onTest(k)} disabled={testing} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-2 text-xs text-[#A1A8B3] transition-colors hover:border-[#E07A5F]/60 hover:text-[#F4A261] disabled:opacity-40">
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Test
        </button>
        {!k.env && (
          <>
            <button data-testid={`conn-toggle-${k.id}`} onClick={() => onToggle(k)} aria-label={k.active ? "Pause this key" : "Use this key"} className="rounded-lg border border-white/10 p-2 text-[#8A919D] transition-colors hover:border-white/25 hover:text-[#F3EFE6]">
              {k.active ? <Pause size={13} /> : <Play size={13} />}
            </button>
            <button data-testid={`conn-delete-${k.id}`} onClick={() => onRemove(k)} aria-label="Remove this key" className="rounded-lg border border-[#E63946]/30 p-2 text-[#E63946] transition-colors hover:bg-[#E63946]/10"><Trash2 size={13} /></button>
          </>
        )}
      </div>
    </li>
  );
};

export default function Connections() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState("");
  const [testing, setTesting] = useState("");

  const load = () => api.connections().then(setData).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const apply = (r) => setData({ openai: r.openai, elevenlabs: r.elevenlabs });

  const add = async (p) => {
    const d = draft[p.id] || {};
    if (!(d.key || "").trim()) return;
    setBusy(p.id);
    try {
      const r = await api.addConnection(p.id, d.label, d.key.trim());
      apply(r);
      setDraft({ ...draft, [p.id]: {} });
      r.test?.ok ? toast.success(`${p.name} key accepted — ${r.test.detail}`) : toast.error(`Stored, but ${p.name} refused it: ${r.test?.detail}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const test = async (k, provider) => {
    setTesting(k.id);
    try {
      const r = k.env ? await api.testEnvConnection(provider) : await api.testConnection(k.id);
      apply(r);
      r.test?.ok ? toast.success(r.test.detail) : toast.error(r.test?.detail || "It was refused");
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(""); }
  };

  const move = async (k, dir, provider) => {
    const ids = (data[provider].keys || []).filter((r) => !r.env).map((r) => r.id);
    const i = ids.indexOf(k.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { apply(await api.reorderConnections(provider, ids)); } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (k) => {
    try {
      apply(await api.patchConnection(k.id, { active: !k.active }));
      toast(k.active ? `${k.label} paused` : `${k.label} is back in the chain`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Remove ${k.label}?\n\nGriot stops using it immediately.`)) return;
    try { apply(await api.deleteConnection(k.id)); toast.success(`${k.label} removed`); } catch (e) { toast.error(errMsg(e)); }
  };

  const wake = async (p) => {
    try { apply(await api.wakeConnections(p.id)); toast.success("Every key is in the chain again"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="text-sm text-[#6E7582]">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="connections-page">
      <header>
        <p className="eyebrow">Connections</p>
        <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">The keys that make him speak</h2>
        <p className="mt-2 max-w-2xl text-sm text-[#8A919D]">
          Griot tries these top to bottom. The first key that answers is the one that pays; a key that is refused —
          wrong, revoked or out of credit — steps aside for a while and the next one picks the conversation up
          mid-sentence. Nothing here is ever sent to a visitor's browser.
        </p>
      </header>

      {PROVIDERS.map((p) => {
        const block = data[p.id] || { keys: [] };
        const rows = block.keys || [];
        const curator = rows.filter((r) => !r.env);
        const resting = rows.some((r) => r.state === "resting");
        const d = draft[p.id] || {};
        return (
          <section key={p.id} data-testid={`provider-card-${p.id}`} className="rounded-2xl border border-white/[0.06] bg-[#1A1D21]">
            <div className="flex flex-wrap items-start justify-between gap-4 p-5 pb-4">
              <div className="min-w-[240px] flex-1">
                <h3 className="font-display text-lg font-medium">{p.name}</h3>
                <p className="mt-0.5 max-w-xl text-sm text-[#8A919D]">{p.role}</p>
              </div>
              <div className="text-right">
                {block.answered ? (
                  <p className="font-mono text-[11px] text-[#2EC4B6]" data-testid={`conn-answered-${p.id}`}>
                    <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#2EC4B6] align-middle" />
                    {block.answered.label} answered {ago(block.answered.at)}
                  </p>
                ) : (
                  <p className="font-mono text-[11px] text-[#6E7582]" data-testid={`conn-answered-${p.id}`}>nothing has been asked of it yet</p>
                )}
                {resting && (
                  <button data-testid={`conn-wake-${p.id}`} onClick={() => wake(p)} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#E9C46A]/40 px-3 py-1.5 text-xs text-[#E9C46A] transition-colors hover:bg-[#E9C46A]/10">
                    <RefreshCw size={12} /> Try them all again
                  </button>
                )}
              </div>
            </div>

            {rows.length === 0 ? (
              <p className="border-t border-white/[0.06] px-5 py-6 text-sm text-[#6E7582]" data-testid={`conn-empty-${p.id}`}>
                No key for {p.name} yet — {p.id === "openai" ? "Griot cannot think or hear without one" : "the real voices stay silent and the understudy speaks"}.
              </p>
            ) : (
              <ul className="divide-y divide-white/[0.06] border-t border-white/[0.06]">
                {rows.map((k, i) => (
                  <KeyRow
                    key={k.id}
                    k={k}
                    i={i}
                    last={i === curator.length - 1 || k.env}
                    testing={testing === k.id}
                    onMove={(kk, dir) => move(kk, dir, p.id)}
                    onTest={(kk) => test(kk, p.id)}
                    onToggle={toggle}
                    onRemove={remove}
                  />
                ))}
              </ul>
            )}

            <div className="border-t border-white/[0.06] p-5" data-testid={`conn-add-form-${p.id}`}>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block w-full sm:w-44">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6E7582]">name it</span>
                  <input
                    data-testid={`conn-label-input-${p.id}`}
                    value={d.label || ""}
                    onChange={(e) => setDraft({ ...draft, [p.id]: { ...d, label: e.target.value } })}
                    placeholder="Lab account"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2.5 text-sm text-[#F3EFE6] placeholder:text-[#4E555F] focus:border-[#E07A5F] focus:outline-none"
                  />
                </label>
                <label className="block min-w-[220px] flex-1">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6E7582]">the key</span>
                  <div className="mt-2 flex items-center rounded-xl border border-white/10 bg-[#121417] px-3 focus-within:border-[#E07A5F]">
                    <input
                      data-testid={`conn-key-input-${p.id}`}
                      type={reveal[p.id] ? "text" : "password"}
                      autoComplete="off"
                      value={d.key || ""}
                      onChange={(e) => setDraft({ ...draft, [p.id]: { ...d, key: e.target.value } })}
                      onKeyDown={(e) => e.key === "Enter" && add(p)}
                      placeholder={p.placeholder}
                      className="w-full bg-transparent py-2.5 font-mono text-sm text-[#F3EFE6] placeholder:font-sans placeholder:text-[#4E555F] focus:outline-none"
                    />
                    <button type="button" data-testid={`conn-reveal-${p.id}`} onClick={() => setReveal({ ...reveal, [p.id]: !reveal[p.id] })} aria-label="Show the key" className="shrink-0 p-1 text-[#6E7582] hover:text-[#A1A8B3]">
                      {reveal[p.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </label>
                <button
                  data-testid={`conn-add-${p.id}`}
                  onClick={() => add(p)}
                  disabled={busy === p.id || !(d.key || "").trim()}
                  className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-5 py-2.5 text-sm font-semibold text-[#121417] transition-colors hover:bg-[#F4A261] disabled:opacity-40"
                >
                  {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add &amp; test
                </button>
              </div>
              <p className="mt-3 text-xs text-[#6E7582]">Find it at {p.where}. It is tested the moment you add it, and it never leaves the server.</p>
            </div>
          </section>
        );
      })}
    </div>
  );
}
